import {PayloadAction, createSlice} from '@reduxjs/toolkit';

export interface ICropedImageAdvertisement {
  file?: File;
  flag?: boolean;
  id?: number;
  link?: string;
  lastModified?: number;
}

export interface IAdminImagesAdvertisement {
  file?: File;
  flag?: boolean;
  id?: number;
  link?: string;
  lastModified?: number;
}

interface IAddPictureAdvertisement {
  imageFile: React.ChangeEvent<HTMLInputElement> | null;
  cropedImage: Array<ICropedImageAdvertisement> | null;
  adminImages: Array<IAdminImagesAdvertisement> | null;
}
interface IImage {
  imageFile: React.ChangeEvent<HTMLInputElement> | null;
}
const initialState: IAddPictureAdvertisement = {
  imageFile: null,
  cropedImage: null,
  adminImages: null,
};

export const addPictureAdvertisement = createSlice({
  name: 'PictureAdvertisement',

  initialState,
  reducers: {
    addingImage: (state, action: PayloadAction<IImage>) => {
      state.imageFile = action.payload.imageFile;
    },
    addingCropedImage: (
      state,
      action: PayloadAction<ICropedImageAdvertisement>,
    ) => {
      state.cropedImage =
        state.cropedImage &&
        state.cropedImage.length > 0 &&
        !state.cropedImage.some(img => img.id === action.payload.id)
          ? [
              ...state.cropedImage,
              {
                file: action.payload.file,
                lastModified: action.payload.lastModified,
                id: action.payload.id,
                flag: action.payload.flag,
                link: action.payload.link,
              },
            ]
          : [
              {
                file: action.payload.file,
                lastModified: action.payload.lastModified,
                id: action.payload.id,
                // flag: action.payload.flag === false ? false : true,
                flag: action.payload.flag,
                link: action.payload.link,
              },
            ];
    },
    addingAdminImages: (
      state,
      action: PayloadAction<IAdminImagesAdvertisement>,
    ) => {
      state.adminImages = state.adminImages
        ? [
            ...state.adminImages,
            {
              file: action.payload.file,
              lastModified: action.payload.lastModified,
              id: action.payload.id,
              flag: action.payload.flag,
              link: action.payload.link,
            },
          ]
        : [
            {
              file: action.payload.file,
              lastModified: action.payload.lastModified,
              id: action.payload.id,
              flag: action.payload.flag,
              link: action.payload.link,
            },
          ];
    },
    clearAdminImages: state => {
      state.adminImages = null;
    },
    clearImage: state => {
      state.imageFile = null;
    },
    clearAllData: state => {
      state.imageFile = null;
      state.cropedImage = null;
      state.adminImages = null;
    },
    changeFlag: (state, action: PayloadAction<number | undefined>) => {
      const cropedImageWithFlag =
        state.cropedImage?.map(item => ({...item, flag: false})) || [];
      const adminImagesWithFlag =
        state.adminImages?.map(item => ({...item, flag: false})) || [];

      const combined = [...cropedImageWithFlag, ...adminImagesWithFlag];

      let indexToFlag: number;

      if (typeof action.payload === 'number') {
        // use the payload index
        indexToFlag = action.payload;
      } else if (combined.length > 0) {
        // find the most recent based on lastModified
        const sorted = combined
          .map((item, idx) => ({...item, _originalIndex: idx})) // keep original index
          .sort(
            (a, b) =>
              (a.lastModified ?? a.file?.lastModified ?? 0) -
              (b.lastModified ?? b.file?.lastModified ?? 0),
          );

        // pick last (most recent)
        indexToFlag = sorted[sorted.length - 1]._originalIndex;
      } else {
        // nothing to flag
        indexToFlag = -1;
      }

      if (indexToFlag >= 0) {
        if (indexToFlag < cropedImageWithFlag.length) {
          cropedImageWithFlag[indexToFlag].flag = true;
        } else {
          const adminIndex = indexToFlag - cropedImageWithFlag.length;
          if (adminIndex < adminImagesWithFlag.length) {
            adminImagesWithFlag[adminIndex].flag = true;
          }
        }
      }

      state.cropedImage = cropedImageWithFlag;
      state.adminImages = adminImagesWithFlag;
    },
    deleteImage: (state, action: PayloadAction<number>) => {
      const allImages = [
        ...(state.cropedImage || []),
        ...(state.adminImages || []),
      ];

      if (action.payload < (state.cropedImage?.length || 0)) {
        const filteredImages = (state.cropedImage || []).filter(
          (_, index) => index !== action.payload,
        );
        state.cropedImage = filteredImages.length > 0 ? filteredImages : null;

        if (
          state.cropedImage &&
          state.cropedImage.length > 0 &&
          !state.cropedImage.some(img => img.flag)
        ) {
          state.cropedImage[0].flag = true;
        }
      } else {
        const adminIndex = action.payload - (state.cropedImage?.length || 0);
        const filteredAdminImages = (state.adminImages || []).filter(
          (_, index) => index !== adminIndex,
        );
        state.adminImages =
          filteredAdminImages.length > 0 ? filteredAdminImages : null;

        if (
          allImages[action.payload]?.flag &&
          state.cropedImage &&
          state.cropedImage.length > 0
        ) {
          state.cropedImage[0].flag = true;
        }
      }
    },
  },
});

export const {
  addingImage,
  clearAllData,
  clearImage,
  addingCropedImage,
  deleteImage,
  changeFlag,
  addingAdminImages,
  clearAdminImages,
} = addPictureAdvertisement.actions;

export default addPictureAdvertisement.reducer;
